import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import Asset, { AssetName } from 'components/asset/asset';

export enum LogoVariant {
    BUMBLE_FULL_HORIZONTAL,
    BUMBLE,
    BUMBLE_PREMIUM_BOXED,
    BUMBLE_VERTICAL,
    BUMBLE_BOOST,
    BUMBLE_COINS,
    BUMBLE_HIVE,
    MODE_DATE,
    MODE_BFF,
    MODE_BIZZ,
    PILL_MODE_BFF,
    PILL_MODE_BIZZ,
    BOOST,
    SPOTLIGHT,
    PREMIUM,
}

export enum LogoColor {
    PRIMARY,
    INHERITED,
}

export const mapLogoVariantToAssetName: Record<LogoVariant, AssetName> = {
    [LogoVariant.BUMBLE_FULL_HORIZONTAL]: 'logo-bumble-full-horizontal',
    [LogoVariant.BUMBLE]: 'logo-bumble',
    [LogoVariant.BUMBLE_PREMIUM_BOXED]: 'logo-bumble-premium-boxed',
    [LogoVariant.BUMBLE_VERTICAL]: 'logo-bumble-full-vertical',
    [LogoVariant.BUMBLE_BOOST]: 'logo-bumble-boost',
    [LogoVariant.BUMBLE_COINS]: 'logo-bumble-coins',
    [LogoVariant.BUMBLE_HIVE]: 'logo-bumble-hive',
    [LogoVariant.MODE_DATE]: 'logo-mode-date',
    [LogoVariant.MODE_BFF]: 'logo-mode-bff',
    [LogoVariant.MODE_BIZZ]: 'logo-mode-bizz',
    [LogoVariant.PILL_MODE_BFF]: 'logo-pill-mode-bff',
    [LogoVariant.PILL_MODE_BIZZ]: 'logo-pill-mode-bizz',
    [LogoVariant.BOOST]: 'logo-boost',
    [LogoVariant.SPOTLIGHT]: 'logo-spotlight',
    [LogoVariant.PREMIUM]: 'logo-premium',
};

type ImageProps = {
    aspectRatio: number;
    defaultHeight?: number;
};

// TODO: after Cosmos provides aspect ratios (DO-1238) use them here directly
// also maybe we'll just remove defaultHeight
export const mapLogoVariantToAspectRatio: Record<LogoVariant, ImageProps> = {
    [LogoVariant.BUMBLE_FULL_HORIZONTAL]: {
        aspectRatio: 171 / 30,
        defaultHeight: 30,
    },
    [LogoVariant.BUMBLE]: {
        aspectRatio: 105 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BUMBLE_PREMIUM_BOXED]: {
        aspectRatio: 160 / 29,
        defaultHeight: 29,
    },
    [LogoVariant.BUMBLE_VERTICAL]: {
        aspectRatio: 132 / 96,
        defaultHeight: 96,
    },
    [LogoVariant.BUMBLE_BOOST]: {
        aspectRatio: 202 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BUMBLE_COINS]: {
        aspectRatio: 189 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BUMBLE_HIVE]: {
        aspectRatio: 34 / 30,
        defaultHeight: 30,
    },
    [LogoVariant.MODE_DATE]: {
        aspectRatio: 64 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.MODE_BFF]: {
        aspectRatio: 37 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.MODE_BIZZ]: {
        aspectRatio: 53 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.PILL_MODE_BFF]: {
        aspectRatio: 44 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.PILL_MODE_BIZZ]: {
        aspectRatio: 52 / 24,
        defaultHeight: 24,
    },
    [LogoVariant.BOOST]: {
        aspectRatio: 82 / 29,
        defaultHeight: 29,
    },
    [LogoVariant.SPOTLIGHT]: {
        aspectRatio: 131 / 29,
        defaultHeight: 29,
    },
    [LogoVariant.PREMIUM]: {
        aspectRatio: 160 / 29,
        defaultHeight: 29,
    },
};

const mapLogoColorToClassname: { [key in LogoColor]: string } = {
    [LogoColor.PRIMARY]: 'logo--color-primary',
    [LogoColor.INHERITED]: 'logo--color-inherited',
};

type LogoProps = {
    variant?: LogoVariant;
    color?: LogoColor;
    children?: never;
    width?: number;
    height?: number;
};

const Logo: FunctionComponent<LogoProps> = ({
    variant = LogoVariant.BUMBLE_FULL_HORIZONTAL,
    color = LogoColor.PRIMARY,
    width,
    height,
}) => {
    const classnames = classNames({
        logo: true,
        [mapLogoColorToClassname[color]]: true,
    });

    let calculatedHeight = height || mapLogoVariantToAspectRatio[variant].defaultHeight;
    let calculatedWidth = width;
    const aspectRatio = mapLogoVariantToAspectRatio[variant].aspectRatio;

    if (calculatedWidth && !calculatedHeight) {
        calculatedHeight = Math.round(calculatedWidth / aspectRatio);
    }

    if (calculatedHeight && !calculatedWidth) {
        calculatedWidth = Math.round(calculatedHeight * aspectRatio);
    }

    const style = calculatedWidth
        ? {
              ['--width' as string]: `${calculatedWidth}px`,
              ['--height' as string]: `${calculatedHeight}px`,
          }
        : undefined;

    return (
        <div
            className={classnames}
            style={style}
            data-qa-icon-name={mapLogoVariantToAssetName[variant]}
        >
            <Asset name={mapLogoVariantToAssetName[variant]} />
        </div>
    );
};

export default Logo;
